/**
 * Hotspot functionality for collection interstitials
 * Handles product card overlays when clicking hotspot triggers
 */

// Position overlay based on hotspot position (desktop only)
function positionHotspotOverlay(hotspot, container, overlay) {
  if (!overlay.classList.contains('active')) return;
  if (window.innerWidth < 1024) return; // Mobile uses center by default

  const containerRect = container.getBoundingClientRect();
  const hotspotRect = hotspot.getBoundingClientRect();
  const productCard = overlay.querySelector('.hotspot-product-card');

  if (!productCard) return;

  // Get product card dimensions
  const cardWidth = productCard.offsetWidth || 200;
  const cardHeight = productCard.offsetHeight || 300;

  // Calculate hotspot position in pixels relative to container
  const hotspotX = hotspotRect.left - containerRect.left;
  const hotspotY = hotspotRect.top - containerRect.top;

  // Start with card centered on hotspot
  let cardCenterX = hotspotX;
  let cardCenterY = hotspotY;

  // Calculate card edges when centered on hotspot
  const halfWidth = cardWidth / 2;
  const halfHeight = cardHeight / 2;
  const padding = 10; // Minimum distance from edges

  // Adjust if overflowing left edge
  if (cardCenterX - halfWidth < padding) {
    cardCenterX = halfWidth + padding;
  }

  // Adjust if overflowing right edge
  if (cardCenterX + halfWidth > containerRect.width - padding) {
    cardCenterX = containerRect.width - halfWidth - padding;
  }

  // Adjust if overflowing top edge
  if (cardCenterY - halfHeight < padding) {
    cardCenterY = halfHeight + padding;
  }

  // Adjust if overflowing bottom edge
  if (cardCenterY + halfHeight > containerRect.height - padding) {
    cardCenterY = containerRect.height - halfHeight - padding;
  }

  // Convert to percentages for CSS
  const finalX = (cardCenterX / containerRect.width) * 100;
  const finalY = (cardCenterY / containerRect.height) * 100;

  // Set CSS variables for overlay position
  overlay.style.setProperty('--overlay-left', finalX + '%');
  overlay.style.setProperty('--overlay-top', finalY + '%');
}

// Initialize hotspot functionality for a carousel
function initHotspotsForCarousel(carousel) {
  if (!carousel) return;

  const clearHotspots = () => {
    carousel.querySelectorAll('.hotspot.active').forEach(hotspot => hotspot.classList.remove('active'));
    carousel.querySelectorAll('.hotspot-overlay.active').forEach(overlay => {
      overlay.classList.remove('active');
      overlay.innerHTML = '';
    });
  };

  // Click handler scoped to this carousel
  const handleCarouselClick = function(e) {
    const trigger = e.target.closest('.hotspot-trigger');

    if (trigger) {
      e.stopPropagation();

      const hotspot = trigger.closest('.hotspot');
      const container = hotspot ? hotspot.closest('[data-image-container]') : null;
      const overlay = container ? container.querySelector('[data-hotspot-overlay]') : null;
      const template = hotspot ? hotspot.querySelector('.hotspot-template') : null;

      if (!hotspot || !overlay || !template) return;

      // Check if this hotspot is already active
      const isActive = hotspot.classList.contains('active');
      clearHotspots();

      // If this hotspot wasn't active, open it
      if (!isActive) {
        hotspot.classList.add('active');

        // Clone product card into overlay
        const clone = template.content.cloneNode(true);
        overlay.innerHTML = '';
        overlay.appendChild(clone);
        overlay.classList.add('active');

        // Position overlay near hotspot on desktop
        positionHotspotOverlay(hotspot, container, overlay);
      }
    }
  };

  // Click outside to close - also scoped to this carousel
  const handleDocumentClick = function(e) {
    const clickedOverlay = e.target.classList.contains('hotspot-overlay');
    const clickedOutsideCarousel = !carousel.contains(e.target);

    if (clickedOutsideCarousel || clickedOverlay) {
      clearHotspots();
    }
  };

  // Reposition on window resize - scoped to this carousel
  const handleResize = function() {
    const activeOverlay = carousel.querySelector('.hotspot-overlay.active');
    if (activeOverlay) {
      const container = activeOverlay.closest('[data-image-container]');
      const activeHotspot = container ? container.querySelector('.hotspot.active') : null;
      if (activeHotspot) {
        positionHotspotOverlay(activeHotspot, container, activeOverlay);
      }
    }
  };

  carousel.addEventListener('click', handleCarouselClick);
  document.addEventListener('click', handleDocumentClick);
  window.addEventListener('resize', handleResize);

  return () => {
    carousel.removeEventListener('click', handleCarouselClick);
    document.removeEventListener('click', handleDocumentClick);
    window.removeEventListener('resize', handleResize);
    clearHotspots();
  };
}

// Export for global use
if (typeof window !== 'undefined') {
  window.initHotspotsForCarousel = initHotspotsForCarousel;
}
